import React, { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { bedtimeAPI } from '@/lib/api';
import { Moon, Play, X } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// The child's own shelf of bedtime stories their parent recorded for them.
const StudentBedtimeShelf = ({ studentName }) => {
  const [watching, setWatching] = useState(null); // { story, url }

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['bedtime-stories-mine'],
    initialPageParam: null,
    queryFn: async ({ pageParam }) =>
      (await bedtimeAPI.mine({ limit: 20, cursor: pageParam || undefined })).data,
    getNextPageParam: (last) => last.next_cursor || undefined,
  });
  const stories = (data?.pages || []).flatMap((p) => p.stories || []);

  const watch = async (story) => {
    try {
      const { data: res } = await bedtimeAPI.playMine(story.id);
      setWatching({ story, url: res.url });
    } catch {
      toast.error('This story is not available right now.');
    }
  };

  return (
    <div className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="student-bedtime-shelf">
      <div className="flex items-center gap-2 mb-4">
        <Moon size={20} style={{ color: '#D4A853' }} />
        <h3 className="text-lg font-bold text-white">Bedtime Stories{studentName ? ` for ${studentName}` : ''}</h3>
      </div>

      {isLoading ? (
        <p className="text-sm" style={{ color: '#94A3B8' }}>Loading…</p>
      ) : stories.length === 0 ? (
        <p className="text-sm" style={{ color: '#94A3B8' }}>
          No bedtime stories yet. Ask your grown-up to record one for you!
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {stories.map((story) => (
            <button key={story.id} onClick={() => watch(story)}
              className="flex items-center gap-3 text-left rounded-lg p-3 transition-all hover:scale-[1.02]"
              style={{ background: 'rgba(212,168,83,0.08)', border: '1px solid rgba(212,168,83,0.3)' }}
              data-testid={`student-bedtime-${story.id}`}>
              <span className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center" style={{ background: '#D4A853', color: '#1e1e2e' }}>
                <Play size={18} />
              </span>
              <span className="min-w-0">
                <span className="block font-bold text-white truncate">{story.title}</span>
                <span className="block text-xs truncate" style={{ color: '#94A3B8' }}>
                  {story.visibility === 'public' ? 'Shared with everyone · ' : ''}{story.story_title ? `${story.story_title} · ` : ''}{formatDate(story.created_date)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {hasNextPage && (
        <div className="text-center mt-3">
          <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
            className="px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-50"
            style={{ color: '#D4A853', border: '1px solid rgba(212,168,83,0.4)' }}>
            {isFetchingNextPage ? 'Loading…' : 'Show more stories'}
          </button>
        </div>
      )}

      {watching && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setWatching(null)}>
          <div className="bg-[#1e1e2e] rounded-xl max-w-2xl w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="font-bold text-sm text-white">{watching.story.title}</p>
              <button onClick={() => setWatching(null)} aria-label="Close" className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            {watching.story.content_type?.startsWith('audio/') ? (
              <div className="p-6 flex flex-col items-center gap-4">
                <Moon size={56} style={{ color: '#D4A853' }} />
                <audio src={watching.url} controls autoPlay className="w-full"
                  onError={() => { setWatching(null); toast.error('Story not available'); }} />
              </div>
            ) : (
              <video src={watching.url} controls autoPlay playsInline className="w-full max-h-[75vh] bg-black"
                onError={() => { setWatching(null); toast.error('Video not available'); }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentBedtimeShelf;
